import { Fix64 } from "../maths/fix64";
import { OfferItem, parseOfferItem } from "./offers";

/**
 * The three currency shops (docs/SHOP.md §9; balance/src/shops.json; owner decisions 2026-09-26, Kingshot's Mystery,
 * Arena and VIP shops): the Mystery Merchant and the Event shop (Marks) and the VIP shop (gems). Every price is
 * computed from a documented base (the item's gem price, docs/SHOP.md §1.2, × the currency's rate) and a discount
 * (`off`), never written by hand (meta/shops.ts). Parsed from the generated bundle's `shops` section; an older bundle
 * has none: `emptyShops` sells nothing.
 */

export interface ShopRates {
  /** Marks per gem of value at level 0; × (1 + level / marksLevelDivisor), the Trading Post's level scaling. */
  readonly marksPerGem: number;
  readonly marksLevelDivisor: number;
}

export interface MerchantPoolEntry { readonly item: OfferItem; readonly weight: number; readonly minLevel: number }

export interface MerchantData {
  readonly unlockAfterLevel: number;
  /** Slots a day (Left: 1 each). */
  readonly slots: number;
  /** The first this many slots are gear rolled as the Trading Post's stock was (market.json stock.weights, its price). */
  readonly gearSlots: number;
  /** Discount percent → weight. */
  readonly discounts: readonly { readonly off: number; readonly weight: number }[];
  /** Gem price of each paid refresh a UTC day, in order; its length is the most paid refreshes a day. */
  readonly refreshGems: readonly number[];
  readonly pool: readonly MerchantPoolEntry[];
  /** MP-08: free items a UTC day (0 or 1): one of the non-gear slots, picked from (player, day), is free until taken. */
  readonly freePerDay: number;
}

/**
 * What event play pays in Marks (docs/SHOP.md §9.2; Valor merged into Marks 2026-09-30), each as a gem VALUE: paid as
 * that value × the Marks rate at the player's level (meta/shops.ts marksForGems), the rate the Event shop prices at,
 * so what a week of play buys there does not move with level.
 */
export interface EventSources {
  /** Per Proving Grounds node (index = node), paid when the node is claimed. */
  readonly provingNodes: readonly Fix64[];
  readonly trialWin: Fix64;
  readonly heroicFirstWin: Fix64;
  readonly farmWin: Fix64;
  readonly farmWinsPerDay: number;
}

export interface ShopStockEntry {
  readonly item: OfferItem;
  /** Discount percent against the base (0 = none). */
  readonly off: number;
  /** Purchases per restock period. */
  readonly limit: number;
  /** Shown from this highest cleared level (Event shop). */
  readonly minLevel: number;
  /** Shown from this VIP level (VIP shop). */
  readonly vipLevel: number;
}

export interface EventShopData {
  readonly unlockAfterLevel: number;
  readonly sources: EventSources;
  readonly featured: { readonly slots: number; readonly off: number; readonly pool: readonly OfferItem[] };
  readonly stock: readonly ShopStockEntry[];
}

export interface VipShopData { readonly stock: readonly ShopStockEntry[] }

export interface ShopsData {
  readonly rates: ShopRates;
  /** A discount badge shows from this percent. */
  readonly showOffFromPercent: number;
  readonly merchant: MerchantData;
  readonly event: EventShopData;
  readonly vip: VipShopData;
}

/** Levels no player reaches: a shop of an old bundle never opens. */
const NEVER = 1_000_000;

export function emptyShops(): ShopsData {
  return {
    rates: { marksPerGem: 0, marksLevelDivisor: 1 }, showOffFromPercent: 0,
    merchant: { unlockAfterLevel: NEVER, slots: 0, gearSlots: 0, discounts: [], refreshGems: [], pool: [], freePerDay: 0 },
    event: { unlockAfterLevel: NEVER, sources: { provingNodes: [], trialWin: Fix64.Zero, heroicFirstWin: Fix64.Zero, farmWin: Fix64.Zero, farmWinsPerDay: 0 }, featured: { slots: 0, off: 0, pool: [] }, stock: [] },
    vip: { stock: [] },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(v: Json, where: string): number {
  if (typeof v === "number" && Number.isInteger(v) && v >= 0) return v;
  throw new TypeError(`${where}: expected a whole number, got ${JSON.stringify(v)}`);
}
/** A non-negative gem value: a whole number or a decimal string ("1.5"). */
function gemValue(v: Json, where: string): Fix64 {
  const f = typeof v === "number" || typeof v === "string" ? Fix64.parse(String(v)) : null;
  if (f === null || f.raw < 0n) throw new TypeError(`${where}: expected a gem value, got ${JSON.stringify(v)}`);
  return f;
}
const PERCENT = 100;
function off(v: Json, where: string): number {
  const n = v === undefined ? 0 : int(v, where);
  if (n >= PERCENT) throw new Error(`${where}: off must be under ${PERCENT}`);
  return n;
}

function stockEntry(e: Json, where: string): ShopStockEntry {
  return {
    item: parseOfferItem(e.item, where), off: off(e.off, where), limit: int(e.limit, where),
    minLevel: e.minLevel === undefined ? 1 : int(e.minLevel, where), vipLevel: e.vipLevel === undefined ? 0 : int(e.vipLevel, where),
  };
}

/** Reads the bundle's `shops` section (gen.py validate_shops has checked it; this re-checks shape and throws otherwise). */
export function parseShops(root: Json): ShopsData {
  const s = root?.shops;
  if (s === undefined) return emptyShops();
  const m = s.merchant, e = s.event;
  const divisor = int(s.rates.marksLevelDivisor, "shops rates");
  if (divisor < 1) throw new Error("shops rates: marksLevelDivisor must be at least 1");
  return {
    rates: { marksPerGem: int(s.rates.marksPerGem, "shops rates"), marksLevelDivisor: divisor },
    showOffFromPercent: int(s.showOffFromPercent, "shops showOffFromPercent"),
    merchant: {
      unlockAfterLevel: int(m.unlockAfterLevel, "merchant"), slots: int(m.slots, "merchant"), gearSlots: int(m.gearSlots, "merchant"),
      discounts: Object.entries(m.discounts as Record<string, Json>).map(([k, w]) => ({ off: off(Number(k), "merchant discounts"), weight: int(w, "merchant discounts") })),
      refreshGems: (m.refreshGems as Json[]).map((g) => int(g, "merchant refreshGems")),
      pool: (m.pool as Json[]).map((p, i) => ({ item: parseOfferItem(p.item, `merchant pool ${i}`), weight: int(p.weight, `merchant pool ${i}`), minLevel: p.minLevel === undefined ? 1 : int(p.minLevel, `merchant pool ${i}`) })),
      freePerDay: m.freePerDay === undefined ? 0 : int(m.freePerDay, "merchant freePerDay"),
    },
    event: {
      unlockAfterLevel: int(e.unlockAfterLevel, "event shop"),
      sources: {
        provingNodes: (e.sources.provingNodes as Json[]).map((v) => gemValue(v, "event sources provingNodes")), trialWin: gemValue(e.sources.trialWin, "event sources trialWin"),
        heroicFirstWin: gemValue(e.sources.heroicFirstWin, "event sources heroicFirstWin"), farmWin: gemValue(e.sources.farmWin, "event sources farmWin"),
        farmWinsPerDay: int(e.sources.farmWinsPerDay, "event sources farmWinsPerDay"),
      },
      featured: { slots: int(e.featured.slots, "event featured"), off: off(e.featured.off, "event featured"), pool: (e.featured.pool as Json[]).map((it, i) => parseOfferItem(it, `event featured ${i}`)) },
      stock: (e.stock as Json[]).map((x, i) => stockEntry(x, `event stock ${i}`)),
    },
    vip: { stock: (s.vip.stock as Json[]).map((x, i) => stockEntry(x, `vip shop stock ${i}`)) },
  };
}
